import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Dumbbell,
  HeartPulse,
  ShieldCheck,
  Trophy,
  Users,
  CalendarDays,
  Target,
  Flame,
  Instagram,
  Facebook,
  Youtube,
  Check,
  UsersRound,
  CreditCard,
} from "lucide-react";
import { siteData } from "../mock";
import PackageCheckoutModal from "../components/PackageCheckoutModal";

const VARIANTS = {
  girls: {
    slug: "girls",
    heading: "GIRLS LACROSSE",
    kicker: "GIRLS LACROSSE · STRENGTH · SPEED · SKILL",
    heroImg: "https://images.unsplash.com/photo-1758646119420-12488edce72c?auto=format&fit=crop&w=1800&q=80",
    lifestyleImg: "https://images.unsplash.com/photo-1540213178569-6f4768f542e8?auto=format&fit=crop&w=1800&q=80",
    photos: {
      strength: "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?auto=format&fit=crop&w=900&q=80",
      conditioning: "https://images.unsplash.com/photo-1550345332-09e3ac987658?auto=format&fit=crop&w=900&q=80",
      lifestyle: "https://images.unsplash.com/photo-1540213178569-6f4768f542e8?auto=format&fit=crop&w=900&q=80",
      lacrosse: "https://images.unsplash.com/photo-1558965747-b707c9a4eb6b?auto=format&fit=crop&w=900&q=80",
    },
    lacrosseLabel: "GIRLS LACROSSE",
  },
  boys: {
    slug: "boys",
    heading: "BOYS LACROSSE",
    kicker: "BOYS LACROSSE · POWER · SPEED · EDGE",
    heroImg: "https://images.unsplash.com/photo-1549287286-f53283f5ad6f?auto=format&fit=crop&w=1800&q=80",
    lifestyleImg: "https://images.unsplash.com/photo-1735847493430-244a0a9610d1?auto=format&fit=crop&w=1800&q=80",
    photos: {
      strength: "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?auto=format&fit=crop&w=900&q=80",
      conditioning: "https://images.unsplash.com/photo-1549288880-687321211d34?auto=format&fit=crop&w=900&q=80",
      lifestyle: "https://images.unsplash.com/photo-1735847493430-244a0a9610d1?auto=format&fit=crop&w=900&q=80",
      lacrosse: "https://images.unsplash.com/photo-1562282305-4cd0bbdcf99a?auto=format&fit=crop&w=900&q=80",
    },
    lacrosseLabel: "BOYS LACROSSE",
  },
};

const pillars = [
  {
    icon: Dumbbell,
    title: "PERSONAL TRAINING",
    text: "Customized programs built around your goals and position.",
  },
  {
    icon: HeartPulse,
    title: "BETTER HEALTH",
    text: "Move better, feel stronger, and stay injury-free all season.",
  },
  {
    icon: ShieldCheck,
    title: "LASTING CONFIDENCE",
    text: "Build discipline and mental toughness — not just results.",
  },
  {
    icon: Trophy,
    title: "REAL RESULTS",
    text: "No shortcuts. Just hard work that shows up on the field.",
  },
  {
    icon: Users,
    title: "ATHLETE DEVELOPMENT",
    text: "Train today. Compete tomorrow. Peak every game.",
  },
];

export default function LacrosseTraining({ variant = "girls" }) {
  const v = VARIANTS[variant] || VARIANTS.girls;
  const brand = siteData.brand.name;
  const letters = brand.split("");
  const [selected, setSelected] = useState(null);

  const pillarPhotos = [
    { key: "strength", label: "STRENGTH", img: v.photos.strength },
    { key: "conditioning", label: "CONDITIONING", img: v.photos.conditioning },
    { key: "lifestyle", label: "LIFESTYLE", img: v.photos.lifestyle },
    { key: "lacrosse", label: v.lacrosseLabel, img: v.photos.lacrosse },
  ];

  const openCheckout = () => {
    setSelected({
      id: "evaluation",
      title: "EVALUATION",
      subtitle: "30-min lacrosse assessment with Chris.",
      price: 50,
      priceNote: "one-time assessment",
      savingsNote: null,
      requiredSessions: 1,
      sessionLabel: "assessment",
      eventTitle: "30 MINUTE ASSESSMENT",
      eventDuration: "30 min",
      calendlyUrl: siteData.calendly.assessment,
    });
  };

  const openTeamCheckout = (pkg) => {
    const hasPlan = Boolean(pkg.installmentPlan);
    setSelected({
      id: pkg.id,
      title: `${pkg.name} · TEAM PACKAGE`,
      subtitle: `${pkg.sessions} sessions for up to ${pkg.maxAthletes} athletes — $${pkg.perPlayerPerSession}/player/session.`,
      price: pkg.price,
      priceNote: `${pkg.sessions} sessions · up to ${pkg.maxAthletes} athletes`,
      savingsNote: pkg.tag === "MAX VALUE" ? "BEST PER-ATHLETE RATE" : null,
      requiredSessions: 1,
      sessionLabel: "team consult",
      eventTitle: "TEAM CONSULT CALL",
      eventDuration: "30 min",
      calendlyUrl: siteData.calendly.assessment,
      installmentPlan: hasPlan
        ? {
            count: pkg.installmentPlan.count,
            amountEach: pkg.installmentPlan.amountEach,
            cadence: pkg.installmentPlan.cadence,
          }
        : null,
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-white/8 bg-[#0a0a0a]/95 sticky top-0 z-40 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-wordmark font-bold text-2xl md:text-3xl tracking-wide">
              {letters.map((l, i) => (
                <span key={i} className={i === 3 ? "deal-yellow" : "text-white"}>
                  {l}
                </span>
              ))}
            </span>
            <span className="font-wordmark text-white/90 text-sm tracking-wide mt-0.5">
              {siteData.brand.tagline}
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-2 text-white/70 hover:text-[#e10600] text-xs tracking-[0.25em] font-heading font-semibold transition-colors"
            >
              <ArrowLeft size={14} />
              MAIN SITE
            </Link>
            <button
              onClick={openCheckout}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.2em] text-xs transition-colors"
            >
              <CalendarDays size={14} />
              BOOK SESSION
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={v.heroImg}
            alt={`${v.heading} action`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/95 via-[#0a0a0a]/80 to-[#0a0a0a]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="deal-yellow font-heading tracking-[0.3em] text-xs md:text-sm mb-4 inline-block">
              {v.kicker}
            </span>
            <h1 className="font-wordmark text-white text-6xl md:text-8xl leading-[0.9] tracking-wide mb-4">
              {variant === "boys" ? (
                <>BOYS <span className="deal-yellow">LACROSSE</span></>
              ) : (
                <>GIRLS <span className="deal-yellow">LACROSSE</span></>
              )}
            </h1>
            <p className="text-white/90 text-lg md:text-xl leading-relaxed max-w-xl mb-3">
              Stronger bodies. Healthier minds.{" "}
              <span className="font-semibold text-white">More confidence.</span>
            </p>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-xl mb-8">
              Sport-specific training that builds field speed, stick skills, and the mental
              toughness to dominate every game.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={openCheckout}
                className="group inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.2em] text-sm transition-colors"
              >
                <CalendarDays size={16} />
                BOOK YOUR SESSION
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#train-with-purpose"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md border border-white/25 hover:border-[#e10600] text-white font-heading font-bold tracking-[0.2em] text-sm transition-colors"
              >
                SEE THE PROGRAM
              </a>
            </div>
          </div>

          {/* Right column stats */}
          <div className="hidden lg:flex flex-col gap-4 items-end">
            <div className="text-right">
              <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-2">
                WHAT WE BUILD
              </p>
              <div className="space-y-3">
                {["SPEED.", "STRENGTH.", "SKILL.", "CONFIDENCE."].map((w, i) => (
                  <p
                    key={w}
                    className={`font-wordmark text-4xl md:text-5xl tracking-wide ${
                      i % 2 === 0 ? "text-white" : "deal-yellow"
                    }`}
                  >
                    {w}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-y border-white/8 bg-[#0d0d0d] py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {pillars.map(({ icon: Icon, title, text }) => (
            <div key={title} className="text-center">
              <div className="w-14 h-14 mx-auto rounded-lg bg-[#e10600]/12 border border-[#e10600]/40 flex items-center justify-center mb-3">
                <Icon size={24} className="text-[#e10600]" strokeWidth={2.2} />
              </div>
              <p className="font-heading tracking-[0.15em] text-xs md:text-sm text-white font-bold mb-2 whitespace-pre-line leading-tight">
                {title}
              </p>
              <p className="text-white/60 text-xs md:text-sm leading-relaxed">
                {text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* TRAIN WITH PURPOSE */}
      <section id="train-with-purpose" className="py-24 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mb-14 items-end">
            <div className="lg:col-span-3">
              <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-3">
                THE PROGRAM
              </p>
              <h2 className="font-wordmark text-white text-6xl md:text-7xl tracking-wide leading-[0.9] mb-5">
                TRAIN WITH{" "}
                <span className="deal-yellow">PURPOSE</span>
              </h2>
              <p className="text-white/80 text-lg leading-relaxed max-w-2xl">
                Personalized training. Real support. A plan built around{" "}
                <span className="font-semibold text-white">you</span> — your position, your goals,
                your season.
              </p>
            </div>
            <div className="lg:col-span-2 flex lg:justify-end">
              <button
                onClick={openCheckout}
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.2em] text-sm transition-colors"
              >
                LEARN MORE
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Photo cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {pillarPhotos.map((p) => (
              <div
                key={p.key}
                className="relative aspect-[3/4] rounded-xl overflow-hidden group border border-white/10 hover:border-[#e10600]/60 transition-colors"
              >
                <img
                  src={p.img}
                  alt={p.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/95 via-[#0a0a0a]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#e10600] text-white">
                    <Target size={13} strokeWidth={2.5} />
                    <span className="font-heading font-bold tracking-[0.15em] text-[11px]">
                      {p.label}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM PACKAGES */}
      <section id="team-packages" className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#120909]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-3">
              BRING THE WHOLE TEAM
            </p>
            <h2 className="font-wordmark text-white text-5xl md:text-7xl tracking-wide leading-[0.9] mb-4">
              TEAM <span className="deal-yellow">PACKAGES</span>
            </h2>
            <p className="text-white/75 text-lg max-w-2xl">
              Off-season prep, mid-season maintenance, or full-season programming for high-school,
              club, and travel teams.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {siteData.teamPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative rounded-2xl p-8 flex flex-col hover-lift ${
                  pkg.tag === "MAX VALUE"
                    ? "bg-[#e10600] text-white border-2 border-[#e10600]"
                    : pkg.tag === "POPULAR"
                    ? "card-surface border border-[#e10600]/50"
                    : "card-surface"
                }`}
              >
                {pkg.tag && (
                  <span
                    className={`absolute -top-3 right-6 px-3 py-1 text-[11px] tracking-[0.2em] font-bold rounded ${
                      pkg.tag === "MAX VALUE"
                        ? "bg-[#0a0a0a] text-[#e10600]"
                        : "bg-[#e10600] text-white"
                    }`}
                  >
                    {pkg.tag}
                  </span>
                )}

                {/* Head */}
                <div className="mb-6">
                  <p
                    className={`font-heading tracking-[0.25em] text-xs mb-2 ${
                      pkg.tag === "MAX VALUE" ? "text-white/80" : "text-white/55"
                    }`}
                  >
                    {pkg.subtitle}
                  </p>
                  <h3
                    className={`font-wordmark text-4xl tracking-wide leading-none ${
                      pkg.tag === "MAX VALUE" ? "text-white" : "text-white"
                    }`}
                  >
                    {pkg.name}
                  </h3>
                </div>

                {/* Roster + sessions row */}
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-heading font-bold tracking-[0.15em] ${
                      pkg.tag === "MAX VALUE"
                        ? "bg-[#0a0a0a]/25 text-white border border-white/30"
                        : "bg-[#e10600]/12 text-[#e10600] border border-[#e10600]/35"
                    }`}
                  >
                    <UsersRound size={12} strokeWidth={2.5} />
                    UP TO {pkg.maxAthletes}
                  </div>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-heading font-bold tracking-[0.15em] ${
                      pkg.tag === "MAX VALUE"
                        ? "bg-[#0a0a0a]/25 text-white border border-white/30"
                        : "bg-white/8 text-white/85 border border-white/15"
                    }`}
                  >
                    <CalendarDays size={12} strokeWidth={2.5} />
                    {pkg.sessions} SESSIONS
                  </div>
                </div>

                {/* Price */}
                <div className="flex-1 mb-6">
                  <div
                    className={`font-display font-bold text-5xl md:text-6xl mb-1 ${
                      pkg.tag === "MAX VALUE" ? "text-white" : "text-white"
                    }`}
                  >
                    ${pkg.price.toLocaleString()}
                  </div>
                  <p
                    className={`text-sm ${
                      pkg.tag === "MAX VALUE" ? "text-white/85" : "text-white/70"
                    }`}
                  >
                    ${pkg.perPlayerPerSession} / player / session
                  </p>

                  {pkg.installmentPlan && (
                    <div
                      className={`mt-4 rounded-lg px-3 py-2.5 inline-flex items-start gap-2 ${
                        pkg.tag === "MAX VALUE"
                          ? "bg-[#0a0a0a]/25 border border-white/25"
                          : "bg-emerald-500/12 border border-emerald-500/40"
                      }`}
                    >
                      <CreditCard
                        size={14}
                        className={`shrink-0 mt-0.5 ${
                          pkg.tag === "MAX VALUE" ? "text-white" : "text-emerald-400"
                        }`}
                        strokeWidth={2.5}
                      />
                      <div className="text-left">
                        <p
                          className={`font-heading font-bold text-[10px] tracking-[0.2em] mb-0.5 ${
                            pkg.tag === "MAX VALUE" ? "text-white" : "text-emerald-400"
                          }`}
                        >
                          OR SPLIT INTO {pkg.installmentPlan.count} PAYMENTS
                        </p>
                        <p
                          className={`text-xs font-semibold ${
                            pkg.tag === "MAX VALUE" ? "text-white/95" : "text-white/90"
                          }`}
                        >
                          ${pkg.installmentPlan.amountEach.toLocaleString()} /{" "}
                          {pkg.installmentPlan.cadence}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-8">
                  {pkg.features.map((f, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-2.5 text-[15px] ${
                        pkg.tag === "MAX VALUE" ? "text-white/95" : "text-white/85"
                      }`}
                    >
                      <Check
                        size={16}
                        className={
                          pkg.tag === "MAX VALUE" ? "text-white shrink-0 mt-1" : "deal-yellow shrink-0 mt-1"
                        }
                        strokeWidth={2.5}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => openTeamCheckout(pkg)}
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-heading font-bold tracking-[0.2em] text-sm transition-colors ${
                    pkg.tag === "MAX VALUE"
                      ? "bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white"
                      : "bg-[#e10600] hover:bg-[#ff1a1a] text-white"
                  }`}
                >
                  REQUEST BOOKING
                  <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Note strip */}
          <div className="mt-10 rounded-xl border border-white/10 bg-white/5 p-5 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-[#e10600] flex items-center justify-center shrink-0">
                <Users size={18} className="text-white" strokeWidth={2.4} />
              </div>
              <div>
                <p className="font-heading text-white text-sm tracking-[0.2em] font-bold mb-1">
                  BIGGER ROSTER? DIFFERENT SCHEDULE?
                </p>
                <p className="text-white/70 text-sm">
                  We build custom programs for teams of 15+ athletes and multi-team clinics. Reach out to build one.
                </p>
              </div>
            </div>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.2em] text-xs transition-colors shrink-0"
            >
              CUSTOM QUOTE
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* LIFESTYLE QUOTE */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={v.lifestyleImg}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <Flame size={36} className="deal-yellow mb-4" />
            <p className="font-wordmark text-white text-5xl md:text-7xl tracking-wide leading-[0.9] mb-5">
              IT'S NOT A DIET.
            </p>
            <p className="font-wordmark deal-yellow text-5xl md:text-7xl tracking-wide leading-[0.9] mb-8">
              IT'S A LIFESTYLE.
            </p>

            <div className="flex flex-wrap items-center gap-3 md:gap-5">
              {["TRAIN", "EAT", "RECOVER", "REPEAT"].map((word, i, arr) => (
                <React.Fragment key={word}>
                  <span className="font-heading font-bold tracking-[0.25em] text-white/90 text-sm md:text-base">
                    {word}
                  </span>
                  {i < arr.length - 1 && (
                    <span className="deal-yellow">/</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-[#e10600] py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-white/80 font-heading tracking-[0.3em] text-xs mb-2">
              YOUR GOALS · OUR MISSION
            </p>
            <p className="font-wordmark text-white text-4xl md:text-5xl tracking-wide leading-tight">
              READY TO TRAIN LIKE AN ATHLETE?
            </p>
          </div>
          <button
            onClick={openCheckout}
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-md bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white font-heading font-bold tracking-[0.2em] text-sm transition-colors"
          >
            <CalendarDays size={16} />
            BOOK YOUR SESSION
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="bg-[#0a0a0a] py-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-wordmark font-bold text-2xl tracking-wide">
              {letters.map((l, i) => (
                <span key={i} className={i === 3 ? "deal-yellow" : "text-white"}>
                  {l}
                </span>
              ))}
            </span>
            <span className="font-wordmark text-white/85 text-xs tracking-wide mt-0.5">
              {siteData.brand.tagline}
            </span>
          </Link>

          <p className="text-white/70 text-xs tracking-[0.2em] font-heading text-center">
            STRONGER · HEALTHIER · CONFIDENT
          </p>

          <div className="flex items-center gap-3">
            {siteData.contact.socials
              .filter((s) => ["Instagram", "Facebook", "Youtube"].includes(s.icon))
              .map((s) => {
                const Icon =
                  s.icon === "Instagram" ? Instagram : s.icon === "Facebook" ? Facebook : Youtube;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-md border border-white/15 flex items-center justify-center text-white/80 hover:text-[#0a0a0a] hover:bg-[#e10600] hover:border-[#e10600] transition-colors"
                    aria-label={s.label}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
          </div>
        </div>
        <p className="text-white/40 text-[11px] tracking-[0.2em] text-center mt-6">
          © {new Date().getFullYear()} THEDEAL FITNESS · Manassas, VA
        </p>
      </footer>

      <PackageCheckoutModal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        item={selected}
      />
    </div>
  );
}
